import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User } from '../types'
import { authApi } from '../services/api'

interface AuthState {
  token: string | null
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  checkAuth: () => Promise<void>
  clearError: () => void
  updateUser: (user: Partial<User>) => void
  hasPermission: (permission: string) => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response: any = await authApi.login({ username, password })
          const { token, user } = response.data || response
          set({
            token,
            user,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error: any) {
          const errMsg = error.response?.data?.error || error.response?.data?.message || '登录失败'
          set({
            error: errMsg,
            isLoading: false,
          })
          throw error
        }
      },

      logout: () => {
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          error: null,
        })
      },

      checkAuth: async () => {
        const { token } = get()
        if (!token) {
          set({ isAuthenticated: false })
          return
        }

        set({ isLoading: true })
        try {
          const response: any = await authApi.me()
          const { user } = response.data || response
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error) {
          set({
            token: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
          })
        }
      },

      clearError: () => {
        set({ error: null })
      },

      updateUser: (userData: Partial<User>) => {
        const { user } = get()
        if (user) {
          set({ user: { ...user, ...userData } })
        }
      },

      hasPermission: (permission: string) => {
        const { user } = get()
        if (!user) return false
        if (user.roleName === 'super_admin') return true
        return user.permissions?.includes(permission) || false
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      merge: (persistedState: any, currentState: any) => {
        const persisted = persistedState as any
        if (persisted?.user && !persisted.user.roleName) {
          return {
            ...currentState,
            token: null,
            user: null,
            isAuthenticated: false,
          }
        }
        return { ...currentState, ...persisted }
      },
    }
  )
)
